import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import {
  BULLETIN_SCALE,
  BULLETIN_SUBJECTS,
  DOMAIN_SCALE,
  bulletinSubjectByKey,
  blockCovers,
  type BulletinSubject,
} from '../../lib/bulletin-subjects.ts';
import {
  PERIODS,
  PERIOD_LABEL,
  buildColumns,
  bulletinTotal,
  formatRatio,
  isValidScore,
  scoreRatio,
  type EvaluationColumn,
  type Ratio,
} from '../../lib/bulletin.ts';
import { downloadCsv, slugify } from '../../lib/csv.ts';
import { studentDisplayName, studentNumbers } from '../../lib/student-roster.ts';
import { live, useStore } from '../../store/store.ts';
import type { Evaluation, Student } from '../../store/types.ts';
import { openModal, toast } from '../../ui/ui-store.ts';
import { StudentModal } from '../students/StudentModal.tsx';

/** A domain's columns; `domain` is '' for a block that has none. */
interface DomainColumns {
  domain: string;
  columns: readonly EvaluationColumn[];
}

function columnsByDomain(
  block: BulletinSubject,
  rows: readonly Evaluation[],
): readonly DomainColumns[] {
  if (block.domains.length === 0) return [{ domain: '', columns: buildColumns(rows) }];

  return block.domains.map((domain) => ({
    domain,
    columns: buildColumns(rows.filter((r) => r.domain === domain)),
  }));
}

/**
 * One editable mark.
 *
 * Kept as its own component so the input's draft text lives with the cell:
 * hoisting it would make every keystroke re-render the whole table, which on
 * the tablet is felt.
 */
function ScoreCell({
  column,
  student,
}: {
  column: EvaluationColumn;
  student: Student;
}) {
  const row = column.byStudent.get(student.id);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const input = useRef<HTMLInputElement>(null);

  // `autofocus` is only honoured on page load, not on an element inserted
  // later, so the cell would need a second tap to take the stylus.
  useEffect(() => {
    if (editing) input.current?.focus();
  }, [editing]);

  /**
   * Opens empty rather than pre-filled.
   *
   * The mark is nearly always being replaced, not edited, and on the tablet
   * the stylus writes into the field directly — having to wipe a "0" first
   * turns a one-gesture correction into three. The old value stays visible as
   * the placeholder so nothing is lost from sight.
   */
  function open() {
    setDraft('');
    setEditing(true);
  }

  function commit() {
    setEditing(false);

    // Left blank means left alone. The field opens empty, so treating that as
    // a zero would wipe a mark every time the teacher tapped the wrong cell.
    const next = draft.trim();
    if (next === '') return;

    if (!isValidScore(next)) {
      toast('Entre un nombre ou « A » pour absent');
      return;
    }
    if (next === (row?.score ?? '')) return;

    const store = useStore.getState();
    if (row) {
      store.setEvaluationScore(row.id, next);
    } else {
      store.addStudentScore(
        {
          subject: column.subject,
          domain: column.domain,
          lessonId: column.lessonId,
          title: column.title,
          max: column.maxRaw,
          period: column.period,
          date: column.date,
        },
        student.id,
        next,
      );
    }
  }

  if (editing) {
    return (
      <td class="gridCell">
        <input
          class="gridInput"
          type="text"
          // No `inputMode="decimal"`: it tells the tablet's handwriting
          // recogniser to expect digits, and a stylus "a" then comes back
          // as a 4 — which silently turns an absence into a mark.
          inputMode="text"
          autoComplete="off"
          autoCorrect="off"
          spellcheck={false}
          ref={input}
          value={draft}
          placeholder={row?.score ?? ''}
          aria-label={`Points de ${studentDisplayName(student.name)} — ${column.title}`}
          onInput={(e) => setDraft(e.currentTarget.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.currentTarget.blur();
            if (e.key === 'Escape') setEditing(false);
          }}
        />
      </td>
    );
  }

  return (
    <td class="gridCell">
      <button class="gridBtn" onClick={open}>
        {row ? row.score : '—'}
      </button>
    </td>
  );
}

export function ClassGrid() {
  // `live()` builds a new array, so it must run outside the selector:
  // useSyncExternalStore treats a fresh reference as a changed snapshot and
  // would re-render forever.
  const all = live(useStore((s) => s.students));
  const evaluations = live(useStore((s) => s.evaluations));
  const selectStudent = useStore((s) => s.selectStudent);

  // The grid is read against the paper class list, so it runs in the same
  // alphabetical order and carries the same numbers.
  const numbers = useMemo(() => studentNumbers(all), [all]);
  const students = useMemo(
    () => [...all].sort((a, b) => (numbers.get(a.id) ?? 0) - (numbers.get(b.id) ?? 0)),
    [all, numbers],
  );

  const [blockKey, setBlockKey] = useState(BULLETIN_SUBJECTS[0]!.key);
  const [domain, setDomain] = useState('');
  const [period, setPeriod] = useState('1');

  const block = bulletinSubjectByKey(blockKey) ?? BULLETIN_SUBJECTS[0]!;

  const rows = useMemo(
    () =>
      evaluations.filter((e) => e.period === period && blockCovers(block, e.subject)),
    [evaluations, period, block],
  );

  const byDomain = useMemo(() => columnsByDomain(block, rows), [block, rows]);

  /**
   * What the table shows.
   *
   * Three shapes, all reduced to the same rows: a domain's tests, one column
   * per domain when "Tous" is picked, or the block's tests when it has no
   * domains at all.
   */
  const showingDomains = block.domains.length > 0 && domain === '';

  const columns: readonly EvaluationColumn[] = showingDomains
    ? []
    : (byDomain.find((d) => d.domain === (block.domains.length ? domain : ''))?.columns ?? []);

  const totalScale = showingDomains || block.domains.length === 0 ? BULLETIN_SCALE : DOMAIN_SCALE;

  function totalFor(student: Student): Ratio {
    if (showingDomains) {
      return bulletinTotal(byDomain.map((d) => scoreRatio(student.id, d.columns)));
    }
    return scoreRatio(student.id, columns);
  }

  /** Header cells, then one row per pupil — shared by the table and the CSV. */
  const headers = showingDomains
    ? byDomain.map((d) => ({ label: d.domain, sub: '', scale: DOMAIN_SCALE }))
    : columns.map((c) => ({
        label: c.title,
        // Only useful when the block merges two subjects; otherwise redundant.
        sub: block.subjects.length > 1 ? c.subject : '',
        scale: c.max,
      }));

  function exportCsv() {
    const head = [
      'Élève',
      ...headers.map((h) => `${h.label} / ${h.scale}`),
      `${PERIOD_LABEL[period]} / ${totalScale}`,
    ];

    const body = students.map((student) => [
      `${numbers.get(student.id) ?? ''} ${studentDisplayName(student.name)}`.trim(),
      ...(showingDomains
        ? byDomain.map((d) => formatRatio(scoreRatio(student.id, d.columns), DOMAIN_SCALE))
        : columns.map((c) => c.byStudent.get(student.id)?.score ?? '')),
      formatRatio(totalFor(student), totalScale),
    ]);

    const name = [
      'evaluations',
      slugify(block.label),
      showingDomains ? 'tous' : slugify(domain || block.label),
      `p${period}`,
    ].join('-');

    downloadCsv(`${name}.csv`, [head, ...body]);
  }

  // The pupil list used to live in a column of its own next to this table.
  // Now that every pupil is a row here, an empty class has to be handled here
  // too, or there would be nowhere left to start from.
  if (students.length === 0) {
    return (
      <div class="card classGrid">
        <div class="empty" style={{ padding: '40px' }}>
          Aucun élève pour l’instant.
          <br />
          <br />
          <button
            class="btn primary"
            onClick={() => openModal('Ajouter un élève', <StudentModal studentId={null} />)}
          >
            + Ajouter mon premier élève
          </button>
        </div>
      </div>
    );
  }

  return (
    <div class="card classGrid">
      <div class="cardHead">
        <div>
          <h3>Vue d’ensemble des évaluations</h3>
          {/* Says in words what the toolbar selects — and the toolbar is
              hidden on paper, so this is what tells the reader what they are
              holding. */}
          <div class="small muted gridCaption">
            {[
              block.label,
              block.domains.length ? domain || 'tous les domaines' : null,
              PERIOD_LABEL[period],
              `${students.length} élève(s)`,
            ]
              .filter(Boolean)
              .join(' • ')}
          </div>
        </div>
      </div>

      <div class="gridTools">
        <select
          value={blockKey}
          aria-label="Matière"
          onChange={(e) => {
            setBlockKey(e.currentTarget.value);
            setDomain('');
          }}
        >
          {BULLETIN_SUBJECTS.map((b) => (
            <option key={b.key} value={b.key}>
              {b.label}
            </option>
          ))}
        </select>

        {block.domains.length > 0 && (
          <select
            value={domain}
            aria-label="Domaine"
            onChange={(e) => setDomain(e.currentTarget.value)}
          >
            <option value="">Tous</option>
            {block.domains.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
        )}

        <select
          value={period}
          aria-label="Période"
          onChange={(e) => setPeriod(e.currentTarget.value)}
        >
          {PERIODS.map((p) => (
            <option key={p} value={p}>
              {PERIOD_LABEL[p]}
            </option>
          ))}
        </select>

        <span class="spacer" />
        <button class="btn soft" onClick={() => window.print()}>
          Imprimer
        </button>
        <button class="btn soft" onClick={exportCsv}>
          Exporter (CSV)
        </button>
      </div>

      {/* The table is drawn even with nothing to grade: its first column is
          how a pupil's file is reached. */}
      {headers.length === 0 && (
        <p class="muted">Aucune évaluation encodée pour cette sélection.</p>
      )}

      <div class="gridScroll">
        <table class="gradeTable">
          <thead>
            <tr>
              <th class="stickyCol">Élève</th>
              {headers.map((h, i) => (
                <th key={`${h.label}-${i}`}>
                  <div>{h.label}</div>
                  {h.sub && <div class="small muted">{h.sub}</div>}
                  <div class="small muted">/ {h.scale}</div>
                </th>
              ))}
              <th class="totalCol">
                <div>{PERIOD_LABEL[period]}</div>
                <div class="small muted">/ {totalScale}</div>
              </th>
            </tr>
          </thead>
          <tbody>
            {students.map((student) => (
              <tr key={student.id}>
                <th class="stickyCol">
                  <button class="linkBtn" onClick={() => selectStudent(student.id)}>
                    <span class="gridNumber">{numbers.get(student.id)}</span>{' '}
                    {studentDisplayName(student.name)}
                  </button>
                </th>

                {showingDomains
                  ? byDomain.map((d) => (
                      <td key={d.domain} class="gridCell">
                        {/* A computed average: clicking it opens that domain
                            rather than pretending to be editable. */}
                        <button class="gridBtn" onClick={() => setDomain(d.domain)}>
                          {formatRatio(scoreRatio(student.id, d.columns), DOMAIN_SCALE)}
                        </button>
                      </td>
                    ))
                  : columns.map((c) => <ScoreCell key={c.key} column={c} student={student} />)}

                <td class="gridCell totalCol">
                  <b>{formatRatio(totalFor(student), totalScale)}</b>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
